import React, { useState, useEffect } from "react";
import HomePage from "./pages/HomePage";
import ProductSearch from "./components/ProductSearch";
import ProductDetailPage from "./pages/ProductDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ComparePage from "./pages/ComparePage"; // 🎯 NEW
import DisclosurePage from "./pages/DisclosurePage"; // 🎯 NEW
import Navigation from "./components/Navigation";
import categories from "./data/categories";
import { createProductSlug, extractMpnFromSlug } from "./utils/slugify";

const App = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      updatePageFromPath(path, search);
    };

    window.addEventListener("popstate", handlePopState);
    updatePageFromPath(window.location.pathname, window.location.search);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const updatePageFromPath = async (path, search = "") => {
    console.log("📍 URL changed to:", path, search);

    const urlParams = new URLSearchParams(search);
    const category = urlParams.get("category") || "";
    setCategoryFilter(category);

    if (path === "/" || path === "") {
      setCurrentPage("home");
      setSelectedProduct(null);
    } else if (path === "/browse") {
      setCurrentPage("browse");
      setSelectedProduct(null);
    } else if (path === "/compare") {
      // 🎯 NEW
      setCurrentPage("compare");
      setSelectedProduct(null);
    } else if (path === "/disclosure") {
      // 🎯 NEW
      setCurrentPage("disclosure");
      setSelectedProduct(null);
    } else if (path.startsWith("/product/")) {
      const slug = path.replace("/product/", "");
      console.log("🔍 Loading product with slug:", slug);
      await fetchProductBySlug(slug);
      setCurrentPage("product");
    } else if (path === "/about") {
      setCurrentPage("about");
      setSelectedProduct(null);
    } else if (path === "/contact") {
      setCurrentPage("contact");
      setSelectedProduct(null);
    }
  };

  const fetchProductBySlug = async (slug) => {
    try {
      setLoading(true);
      const mpn = extractMpnFromSlug(slug);
      console.log("📦 Extracted MPN:", mpn);

      const API_URL =
        "https://d30qjh1hmqs4s9.cloudfront.net/eurooptic-product-search";
      const response = await fetch(`${API_URL}?mpn=${mpn}`);
      const data = await response.json();

      console.log("🔍 API Response:", data);

      if (data.success && data.products && data.products.length > 0) {
        console.log("✅ Product found:", data.products[0]);
        setSelectedProduct(data.products[0]);
      } else {
        console.log("❌ Product not found, redirecting to browse");
        navigateTo("/browse");
      }
    } catch (error) {
      console.error("❌ Error fetching product:", error);
      navigateTo("/browse");
    } finally {
      setLoading(false);
    }
  };

  const navigateTo = (path, state = {}) => {
    console.log("🚀 Navigating to:", path);
    window.history.pushState(state, "", path);
    const [pathname, search] = path.split("?");
    updatePageFromPath(pathname, search ? `?${search}` : "");
    // GA4 page view tracking
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
      });
    }
  };

  const handleCategoryClick = (categoryId) => {
    console.log("🏷️ Category clicked:", categoryId);
    setSearchTerm("");
    if (categoryId) {
      navigateTo(`/browse?category=${encodeURIComponent(categoryId)}`);
    } else {
      navigateTo("/browse");
    }
  };

  const handleProductClick = (product) => {
    console.log("🖱️ Product clicked:", product);
    const slug = createProductSlug(product);
    console.log("🔗 Created slug:", slug);
    setSelectedProduct(product);
    navigateTo(`/product/${slug}`);
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigateTo("/browse");
    }
  };

  const handleNavigate = (page) => {
    const routes = {
      home: "/",
      browse: "/browse",
      compare: "/compare", // 🎯 NEW
      about: "/about",
      contact: "/contact",
      disclosure: "/disclosure", // 🎯 NEW
    };
    navigateTo(routes[page] || "/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-50 dark:via-gray-100 dark:to-gray-50 flex items-center justify-center">
        <div className="text-white dark:text-gray-900 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-300 dark:text-gray-700">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-50 dark:via-gray-100 dark:to-gray-50">
      <Navigation currentPage={currentPage} setCurrentPage={handleNavigate} />
      {currentPage === "home" && (
        <HomePage
          categories={categories}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearch={handleSearch}
          handleCategoryClick={handleCategoryClick}
          setCurrentPage={handleNavigate}
        />
      )}
      {currentPage === "browse" && (
        <ProductSearch
          onProductClick={handleProductClick}
          initialSearchTerm={searchTerm}
          initialCategory={categoryFilter}
        />
      )}
      {currentPage === "compare" && <ComparePage />} {/* 🎯 NEW */}
      {currentPage === "product" && selectedProduct && (
        <ProductDetailPage
          product={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          setCurrentPage={handleNavigate}
        />
      )}
      {currentPage === "about" && <AboutPage setCurrentPage={handleNavigate} />}
      {currentPage === "contact" && (
        <ContactPage setCurrentPage={handleNavigate} />
      )}
      {currentPage === "disclosure" && <DisclosurePage />} {/* 🎯 NEW */}
    </div>
  );
};

export default App;
